/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Styles
import input from "../../../../assets/styles/components/input.module.css";
import select from "../../../../assets/styles/components/select.module.css";
import styles from "./FormProducts.module.css";

// Icons & Components
import Button from "../../../../components/Button/Button";
import ProductIbpt from "./AddProductsModal/ProductIbpt";
import ModalGeneric from "../../../../components/ModalGeneric/ModalGeneric";
import TypeProduct from "./TypeProduct/TypeProduct";

// Services & Utils
import {
  getProduct,
  importExistingProduct,
  loadAllCategoryProduct,
  loadTypeProduct,
  loadUnidadeMedida,
  registerOrEditProduct,
  searchExistenceBarcode,
  searchExistenceSKU,
} from "../../../../services/products";
import { formatCurrencyInput, parseCurrencyToNumber } from "../../../../utils/useMask";
import useStore from "./Values";

// Types
import {
  type EditProductInputs,
  type propsToRegisterNewProduct,
  type TypeCategoryProduct,
  type typeProduct,
  type unitMeasurement,
} from "../../../../types/products";

interface FormProductsProps {
  isEdit: boolean;
  setIsEdit: (value: boolean) => void;
  uid?: string;
}

const FormProducts = ({ isEdit, setIsEdit, uid }: FormProductsProps) => {
  const { register, handleSubmit, reset, watch, setValue, getValues } = useForm<EditProductInputs>();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const setCopyProduct = useStore((state) => state.setCopyProduct);
  const copyProduct = useStore((state) => state.copyProduct);

  const [priceVisual, setPriceVisual] = useState(""); 
  const [searchSKUOnBlur, setSearchSKUOnBlur] = useState("");
  const [searchBarcodeOnBlur, setSearchBarcodeOnBlur] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [valueDropDown, setValueDropDown] = useState(""); 
  
  const [controlSlide, setControlSlide] = useState({
    Revenda: { control: 0, value: "" },
    "Matéria prima": { control: 0, value: "" },
    "Fabricação própria": { control: 0, value: "" },
  });

  const [
    unidadeMedida,
    loadTypeProducts,
    existProductSKU,
    existProductBarcode,
    categoryProduct,
    product,
  ] = useQueries({
    queries: [
      { queryKey: ["loadUnidadeMedida"], queryFn: loadUnidadeMedida, refetchOnWindowFocus: false },
      { queryKey: ["loadTypeProduct"], queryFn: loadTypeProduct, refetchOnWindowFocus: false },
      {
        queryKey: ["searchExistenceSKU", searchSKUOnBlur],
        queryFn: () => searchExistenceSKU(searchSKUOnBlur, uid || ""),
        enabled: !!searchSKUOnBlur,
      },
      {
        queryKey: ["searchExistenceBarcode", searchBarcodeOnBlur],
        queryFn: () => searchExistenceBarcode(searchBarcodeOnBlur),
        enabled: !!searchBarcodeOnBlur,
        select: (response: any) => response.data,
      },
      { queryKey: ["loadAllCategoryProduct"], queryFn: loadAllCategoryProduct, refetchOnWindowFocus: false },
      {
        queryKey: ["getProduct", uid, copyProduct],
        queryFn: () => getProduct(uid! || copyProduct!, !!copyProduct),
        enabled: !!uid || !!copyProduct,
        refetchOnWindowFocus: false,
      },
    ],
  });

  const hasConflict =
    (existProductSKU.data && existProductSKU.data.status === 'EXISTS_IN_ESTABLISHMENT') ||
    (!existProductBarcode.isLoading && existProductBarcode.data);

  useEffect(() => {
    if (existProductSKU.isSuccess && existProductSKU.data) {
      const { status } = existProductSKU.data as any;
      if (status === 'EXISTS_IN_PROJECT') {
        setShowImportModal(true);
      }
    }
  }, [existProductSKU.isSuccess, existProductSKU.data]);

  const populateForm = useCallback((data: any) => {
    const {
      preco,
      nextSKU,
      tbl_produto: {
        codigo_de_barras,
        codigo_sku,
        tags,
        titulo,
        uid_tipo_unidade_medida,
        uid_produto_categoria,
        tbl_tipo_produtos,
        uid_produto_ibpt,
      },
    } = data;

    const formattedPrice = preco ? formatCurrencyInput(Number(preco).toFixed(2)) : "";
    setPriceVisual(formattedPrice);

    if (uid_produto_ibpt) setValueDropDown(uid_produto_ibpt);

    setControlSlide((prev) => {
        const newSlides = { ...prev };
        Object.keys(newSlides).forEach((key) => {
             // @ts-ignore
            newSlides[key].control = 0;
        });
        
        tbl_tipo_produtos?.forEach((t: any) => {
             // @ts-ignore
            if (newSlides[t.titulo]) {
                 // @ts-ignore
                newSlides[t.titulo] = { control: 1, value: t.uid_tipo_produto }
            }
        });
        return newSlides;
    });

    reset({
      codigo_sku: copyProduct ? nextSKU : codigo_sku,
      titulo: titulo,
      uid_tipo_unidade_medida: uid_tipo_unidade_medida,
      codigo_de_barras: copyProduct ? "" : codigo_de_barras,
      tags: tags,
      price: formattedPrice,
      uid_produto_categoria: uid_produto_categoria,
      unidade_entrada_nfe: "",
    });

  }, [copyProduct, reset]);

  useEffect(() => {
    if (product.data && product.data.data) {
      populateForm(product.data.data);
    }
  }, [product.data, populateForm]);

  const handleCancelEdit = () => {
    setIsEdit(false);
    if (product.data && product.data.data) {
      populateForm(product.data.data);
      toast.info("Edição cancelada. Dados restaurados.");
    }
  };

  const { mutate } = useMutation({
    mutationFn: async (data: propsToRegisterNewProduct) => {
      const res = await registerOrEditProduct(data);
      if (!res.data?.uid_produto) throw new Error("Erro ao obter UID do produto.");
      return res;
    },
    onSuccess: async (data) => {
      toast.success(data?.message || "Sucesso!");
      setIsEdit(false);
      if (copyProduct) setCopyProduct(null);
      if (!uid) navigate(-1);
      
      queryClient.invalidateQueries({ queryKey: ["loadProducts"] });
      if (uid) queryClient.invalidateQueries({ queryKey: ["getProduct", uid] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Erro desconhecido!");
    },
  });

  const { mutate: mutateImport } = useMutation({
    mutationFn: importExistingProduct,
    onSuccess: (res) => {
      toast.success(res.message || "Importado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["loadProducts"] });
      navigate(-1);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || "Erro ao importar."),
  });

  const onSubmit: SubmitHandler<EditProductInputs> = async (data) => {
    if (hasConflict) {
      toast.error("Corrija os erros de antes de salvar.");
      return;
    }

    const isOnlyMateriaPrima =
      controlSlide["Matéria prima"].control === 1 &&
      controlSlide["Revenda"].control === 0 &&
      controlSlide["Fabricação própria"].control === 0;

    const ValueTypeProduct = Object.values(controlSlide)
      .filter((v) => v.control === 1)
      .map((v) => v.value);

    if (!data.codigo_sku) return toast.error("Preencha o SKU");
    if (!data.titulo) return toast.error("Preencha o título");
    if (!data.uid_tipo_unidade_medida) return toast.error("Selecione a medida");
    if (ValueTypeProduct.length === 0) return toast.error("Selecione o tipo do produto");

    const numericPrice = parseCurrencyToNumber(priceVisual);

    mutate({
      codigo_sku: data.codigo_sku,
      titulo: data.titulo,
      uid_tipo_unidade_medida: data.uid_tipo_unidade_medida,
      codigo_de_barras: data.codigo_de_barras || 0,
      tags: data.tags,
      tipo_produto: ValueTypeProduct,
      preco: String(numericPrice),
      uid_produto_categoria: data.uid_produto_categoria,
      uid_produto_ibpt: valueDropDown,
      uid_produto: uid,
    });
  };

  return (
    <form className={styles.formDate} onSubmit={handleSubmit(onSubmit)}>
      <section className={styles.containerRows}>
        <div className={styles.row}>
          <div className={styles.fieldGroup} style={{ flex: 1 }}>
            <label htmlFor="codigo_sku">Código SKU</label>
            <input
              className={input.input}
              type="number" 
              {...register("codigo_sku")}
              placeholder={!isEdit ? "" : "Informe o SKU"}
              onBlur={(e) => setSearchSKUOnBlur(e.target.value)}
              readOnly={!isEdit}
            />
            {existProductSKU.data?.status === 'EXISTS_IN_ESTABLISHMENT' && (
              <span className={styles.alertProductAlreadyRegistered}>
                  {`Em uso por: ${existProductSKU.data.productFound.titulo}. Próximo livre: ${existProductSKU.data.nextSKU}`}
              </span>
            )}
          </div>

          <div className={styles.fieldGroup} style={{ flex: 2 }}>
            <label htmlFor="titulo">Título do produto</label>
            <input
              className={input.input}
              type="text"
              {...register("titulo")}
              placeholder={!isEdit ? "" : "Informe o título"}
              readOnly={!isEdit}
            />
          </div>

          <div className={styles.fieldGroup} style={{ flex: 1 }}>
            <label htmlFor="typeMeasurement">Tipo de medida</label>
            <select
              className={`${input.input} ${select.select}`}
              {...register("uid_tipo_unidade_medida")}
              disabled={!isEdit}
              data-readonly={!isEdit}
            >
              <option value="">{!isEdit ? "-" : "Selecionar"}</option>
              {unidadeMedida.data?.loadUnidadeMedida.map((unit: unitMeasurement) => (
                <option key={unit.uid_tipo_unidade_medida} value={unit.uid_tipo_unidade_medida}>
                  {unit.titulo}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.gridContent}>
          <div className={styles.groupRows}>
            <div className={styles.row}>
              <div className={styles.fieldGroup} style={{ flex: 1 }}>
                <label htmlFor="codeBar">Código de barras</label>
                <input
                  className={input.input}
                  type="number"
                  {...register("codigo_de_barras")}
                  placeholder={!isEdit ? "" : "Informe o código"}
                  maxLength={13}
                  onBlur={(e) => setSearchBarcodeOnBlur(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  readOnly={!isEdit}
                />
                {!existProductBarcode.isLoading && existProductBarcode.data && (
                  <span className={styles.alertProductAlreadyRegistered}>
                    {`Registrado para ${existProductBarcode.data.titulo}`}
                  </span>
                )}
              </div>

              <div className={styles.fieldGroup} style={{ flex: 1 }}>
                <label htmlFor="category">Categoria de produto</label>
                <select
                  className={`${input.input} ${select.select}`}
                  {...register("uid_produto_categoria")}
                  disabled={!isEdit}
                  data-readonly={!isEdit}
                >
                  <option value="">{!isEdit ? "-" : "Selecione"}</option>
                  {categoryProduct.data?.map((category: TypeCategoryProduct) => (
                    <option key={category.uid_produto_categoria} value={category.uid_produto_categoria}>
                      {category.titulo}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.fieldGroup} style={{ flex: 1 }}>
                <label htmlFor="price">Preço</label>
                <input
                  className={input.input}
                  type="text"
                  inputMode="numeric"
                  placeholder={!isEdit ? "" : "0,00"}
                  value={priceVisual} 
                  onChange={(e) => {
                      const formatted = formatCurrencyInput(e.target.value);
                      setPriceVisual(formatted); 
                      setValue("price", formatted); 
                  }}
                  readOnly={!isEdit}
                  maxLength={15}
                />
              </div>
            </div>
          </div>

          <div className={styles.contentTypeProduct}>
            <label>Tipo de produto</label>
            <div className={styles.slidesButtons}>
              {loadTypeProducts.data?.loadTypeProducts.map((typeProducts: typeProduct) => (
                <TypeProduct
                  key={typeProducts.titulo}
                  typeProducts={typeProducts}
                  controlSlide={controlSlide}
                  setControlSlide={setControlSlide}
                  disabled={!isEdit}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={styles.row}>
          <ProductIbpt
            valueDropDown={valueDropDown}
            setValueDropDown={setValueDropDown}
            style={{ flex: 1 }}
            disabled={!isEdit}
            readOnly={!isEdit}
          />
        </div>

        <div className={styles.row}>
          <div className={styles.fieldGroup} style={{ flex: 1 }}>
            <label>Tags</label>
            <textarea
              className={input.input}
              rows={6}
              {...register("tags")}
              placeholder={!isEdit ? "" : "Digite as tags..."}
              readOnly={!isEdit}
            ></textarea>
          </div>
        </div>

        {isEdit && (
          <div className={styles.buttonSave}>
            <Button
              variant="contain"
              type="submit"
              disabled={hasConflict}
              title={hasConflict ? "Corrija erros de SKU/Barcode" : "Salvar produto"}
            >
              Salvar
            </Button>
            {!!uid && (
              <Button variant="contain" type="button" typeStyle={["cancel"]} onClick={handleCancelEdit}>
                Cancelar
              </Button>
            )}
          </div>
        )}

        {showImportModal && existProductSKU.data && (
          <ModalGeneric
            modalGenericCustom={styles.containerModalExistProductSKU}
            contentClass={styles.contentModalExistProductSKU}
            title="Produto encontrado no projeto"
            onClick={() => {
              setValue("codigo_sku", "");
              setSearchSKUOnBlur("");
              setShowImportModal(false);
            }}
          >
            <p>
              O SKU <strong>{getValues("codigo_sku")}</strong> já existe...
            </p>
            <div className={styles.modalButtons}>
              <Button
                variant="contain"
                onClick={() => {
                  mutateImport({ uid_produto: existProductSKU.data.productFound.uid_produto });
                  setShowImportModal(false);
                }}
              >
                Importar produto
              </Button>
              <Button
                variant="contain"
                onClick={() => {
                  const nextSku = String(existProductSKU.data.nextSKU);
                  setValue("codigo_sku", nextSku);
                  setSearchSKUOnBlur(nextSku);
                  setShowImportModal(false);
                }}
              >
                Usar próximo SKU ({existProductSKU.data.nextSKU})
              </Button>
              <Button
                  variant="contain"
                  typeStyle={["cancel"]}
                  onClick={() => {
                      setValue("codigo_sku", "");
                      setSearchSKUOnBlur("");
                      setShowImportModal(false);
                  }}
              >
                  Cancelar
              </Button>
            </div>
          </ModalGeneric>
        )}
      </section>
    </form>
  );
};

export default FormProducts;